import React, { ReactNode, RefObject, useContext, useEffect, useRef } from 'react';
import cn from 'classnames';
import { AppContext } from 'contextStore/context';
import debounce from 'lodash/debounce';

interface ITableWrapper {
  children: ReactNode;
  className?: string;
  useStickyActions?: boolean;
  updateActionShadow: (value: boolean) => void;
  hideActionShadow: boolean;
  hasTableScroll: boolean;
  updateTableScroll: (value: boolean) => void;
  tableRef: RefObject<HTMLTableElement>;
}

export const TableWrapper = ({
  children,
  className,
  useStickyActions,
  updateActionShadow,
  hideActionShadow,
  updateTableScroll,
  tableRef,
}: ITableWrapper) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const additionalClass = className || '';

  const { documentDirection } = useContext(AppContext);

  useEffect(() => {
    if (!useStickyActions) {
      return;
    }
    const tableWrapper = tableWrapperRef.current;
    const table = tableRef.current;

    const debounceInstance = debounce((entries) => {
      if (!table) {
        return;
      }

      for (const entry of entries) {
        const { contentRect } = entry;
        const divWidth = contentRect.width;

        const tableWidth = table.offsetWidth;

        const hasHorizontalScroll = tableWidth > Math.round(divWidth);

        const isScrollEnd = isHorizontalScrollAtEnd();

        if (hasHorizontalScroll) {
          updateTableScroll(true);
          updateActionShadow(isScrollEnd);
        } else {
          updateTableScroll(false);
          updateActionShadow(true);
        }
      }
    }, 300);

    const resizeObserver = new ResizeObserver(debounceInstance);

    if (tableWrapper) {
      resizeObserver.observe(tableWrapper);
    }

    return () => {
      if (tableWrapper) {
        debounceInstance.cancel();
        resizeObserver.unobserve(tableWrapper);
      }
    };
  }, [tableRef, updateActionShadow, updateTableScroll, useStickyActions]);

  const isHorizontalScrollAtEnd = () => {
    if (tableWrapperRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = tableWrapperRef.current;

      return scrollLeft + clientWidth >= scrollWidth;
    }

    return false;
  };

  const tableWrapperScrollHandler = (e: React.UIEvent<HTMLDivElement>) => {
    if (!useStickyActions) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
    const tolerance = 1;
    const maxScrollLeft = scrollWidth - clientWidth - tolerance;

    const currentActionShadowState =
      documentDirection === 'rtl' ? -Math.floor(scrollLeft) >= maxScrollLeft : Math.ceil(scrollLeft) >= maxScrollLeft;

    if (currentActionShadowState !== hideActionShadow) {
      updateActionShadow(currentActionShadowState);
    }
  };

  return (
    <div ref={tableWrapperRef} onScroll={tableWrapperScrollHandler} className={cn('table__wrap', additionalClass)}>
      {children}
    </div>
  );
};
